/**
 * React Context for managing injected wallet connections.
 *
 * This context provides an alternative to @web3-react for wallets
 * that have postMessage compatibility issues (Rabby, Brave, etc).
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { WalletType } from './walletDetection';
import {
  createInjectedConnector,
  InjectedConnector,
  EIP1193Provider,
} from './injectedConnector';

interface InjectedWalletState {
  isConnected: boolean;
  accounts: string[];
  chainId: number | undefined;
  walletType: WalletType | undefined;
  error: Error | undefined;
}

interface InjectedWalletContextValue extends InjectedWalletState {
  connect: (walletType: WalletType) => Promise<void>;
  disconnect: () => void;
  getProvider: () => EIP1193Provider | null;
}

const defaultState: InjectedWalletState = {
  isConnected: false,
  accounts: [],
  chainId: undefined,
  walletType: undefined,
  error: undefined,
};

const InjectedWalletContext = createContext<InjectedWalletContextValue | null>(
  null
);

export function InjectedWalletProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InjectedWalletState>(defaultState);
  // Store connector instance in a ref to persist across renders without module-level state
  const connectorRef = useRef<InjectedConnector | null>(null);

  const connect = useCallback(async (walletType: WalletType) => {
    try {
      // Disconnect existing connector if any
      if (connectorRef.current) {
        connectorRef.current.disconnect();
      }

      // Create and connect new connector
      connectorRef.current = createInjectedConnector(walletType);
      const result = await connectorRef.current.connect();

      setState({
        isConnected: true,
        accounts: result.accounts,
        chainId: result.chainId,
        walletType,
        error: undefined,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error as Error,
      }));
      throw error;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (connectorRef.current) {
      connectorRef.current.disconnect();
      connectorRef.current = null;
    }
    setState(defaultState);
  }, []);

  const getProvider = useCallback(() => {
    return connectorRef.current?.getProvider() ?? null;
  }, []);

  return (
    <InjectedWalletContext.Provider
      value={{
        ...state,
        connect,
        disconnect,
        getProvider,
      }}
    >
      {children}
    </InjectedWalletContext.Provider>
  );
}

export function useInjectedWallet(): InjectedWalletContextValue {
  const context = useContext(InjectedWalletContext);
  if (!context) {
    throw new Error(
      'useInjectedWallet must be used within InjectedWalletProvider'
    );
  }
  return context;
}

/**
 * Hook to check if we should use the injected wallet context
 * instead of web3-react (for non-MetaMask wallets).
 */
export function useInjectedWalletIfAvailable(): InjectedWalletContextValue | null {
  return useContext(InjectedWalletContext);
}
